import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppSummaryStatisticsService } from '../../../api/api-services/app-summary-statistics.service';
import { AppGrowthStatisticsService } from '../../../api/api-services/app-growth-statistics.service';
import { IAppSummaryStatisticsVm } from '../../../api/app-summary-statistics-vm.model';
import { IAppGrowthStatisticsVm } from '../../../api/app-growth-statistics-vm.model';
import { IGetStatisticsUm } from '../../../api/get-statistics-um.model';
import { StatisticsGroupingType } from '../../../api/statistics-grouping-type.model';
import { MOBILE_LAYOUT_QUERY } from '../../shared/services/mobile-shell.service';

// PrimeNG
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from 'primeng/tabview';

@Component({
  selector: 'app-statistics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChartModule,
    CardModule,
    CalendarModule,
    ButtonModule,
    DropdownModule,
    ProgressSpinnerModule,
    TabViewModule
  ],
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.scss'
})
export class StatisticsComponent implements OnInit {
  private _appSummaryStatisticsService = inject(AppSummaryStatisticsService);
  private _appGrowthStatisticsService = inject(AppGrowthStatisticsService);

 
  public growthStatistics: IAppGrowthStatisticsVm | null = null;
  public summaryStatistics: IAppSummaryStatisticsVm | null = null;
  public loadingSummary = false;
  public loadingGrowth = false;

  
  public startDate: Date | null = null;
  public endDate: Date | null = null;
  public selectedGrouping: StatisticsGroupingType = StatisticsGroupingType.Monthly;

  public groupingOptions = [
    { label: 'Dzienne', value: StatisticsGroupingType.Daily },
    { label: 'Tygodniowe', value: StatisticsGroupingType.Weekly },
    { label: 'Miesięczne', value: StatisticsGroupingType.Monthly }
  ];

  
  public userGrowthBarChartData: any;
  public userGrowthLineChartData: any;
  public activeUsersComparisonChartData: any;
  public subscriptionPieChartData: any;
  public accessSourcePieChartData: any;
  public accessSourceGrowthChartData: any;

 
  public barChartOptions: any;
  public lineChartOptions: any;
  public pieChartOptions: any;
  public accessSourcePieOptions: any;
  public accessSourceGrowthOptions: any;

  ngOnInit(): void {
    this.initializeDateRange();
    this.initializeChartOptions();
    this.loadSummaryStatistics();
    this.loadGrowthStatistics();
  }

  private initializeDateRange(): void {
    
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    
    this.endDate = end;
    this.startDate = start;
  }

  private initializeChartOptions(): void {
    // On a phone a legend next to a pie squeezes the pie and cuts the plan names: it goes below the pie, with
    // smaller colour boxes (40 is the chart.js default) leaving the width to the names.
    const phone = window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
    const pieLegendPosition = phone ? 'bottom' : 'right';
    const pieLegendBoxWidth = phone ? 12 : 40;

    // Opcje dla wykresu kołowego źródeł dostępu
    this.accessSourcePieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: pieLegendPosition,
          labels: {
            font: { size: 12 },
            boxWidth: pieLegendBoxWidth
          }
        },
        title: {
          display: true,
          text: 'Źródła dostępu użytkowników',
          font: { size: 16 }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = this.summaryStatistics ? 
                (this.summaryStatistics.paidSubscriptionsCount + this.summaryStatistics.bookAccessCount) : 1;
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Opcje dla wykresu wzrostu źródeł dostępu
    this.accessSourceGrowthOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: { size: 12 }
          }
        },
        title: {
          display: true,
          text: 'Wzrost według źródeł dostępu',
          font: { size: 16 }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1 }
        }
      }
    };
   
    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: { size: 12 }
          }
        },
        title: {
          display: true,
          text: 'Przyrost użytkowników w okresie',
          font: { size: 16 }
        },
        tooltip: {
          callbacks: {
            afterLabel: (context: any) => {
              if (!this.growthStatistics) return [];
              const index = context.dataIndex;
              const isActive = context.dataset.label.includes('aktywni');
              const stat = isActive 
                ? this.growthStatistics.activeUserGrowthStatistics?.[index]
                : this.growthStatistics.userGrowthStatistics?.[index];
              
              if (!stat) return [];
              
              return [
                `Łącznie: ${stat.userCount}`,
                `Zmiana: ${stat.percentageChange.toFixed(1)}%`,
                `Przyrost: ${stat.growthComparedToPrevious}`
              ];
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1 }
        }
      }
    };

   
    this.lineChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: { size: 12 }
          }
        },
        title: {
          display: true,
          text: 'Łączna liczba użytkowników w czasie',
          font: { size: 16 }
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    };

  
    this.pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: pieLegendPosition,
          labels: {
            font: { size: 12 },
            boxWidth: pieLegendBoxWidth
          }
        },
        title: {
          display: true,
          text: 'Rozkład kupionych abonamentów',
          font: { size: 16 }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const activeCount = this.summaryStatistics?.subscriptionStatistics?.[context.dataIndex]?.activeCount || 0;
              return [
                `${label}: ${value}`,
                `Aktywnych: ${activeCount}`
              ];
            }
          }
        }
      }
    };
  }

  public loadSummaryStatistics(): void {
    this.loadingSummary = true;

    this._appSummaryStatisticsService.get().subscribe({
      next: (data) => {
        this.summaryStatistics = data;
        this.loadingSummary = false;
      },
      error: (error) => {
        console.error('Error loading summary statistics:', error);
        this.loadingSummary = false;
      }
    });
  }

  public loadGrowthStatistics(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.loadingGrowth = true;

    const request: IGetStatisticsUm = {
      startDate: this.startDate,
      endDate: this.endDate,
      groupingType: this.selectedGrouping
    };

    this._appGrowthStatisticsService.post(request).subscribe({
      next: (data) => {
        this.growthStatistics = data;
        this.prepareCharts();
        this.loadingGrowth = false;
      },
      error: (error) => {
        console.error('Error loading growth statistics:', error);
        this.loadingGrowth = false;
      }
    });
  }

  private prepareCharts(): void {
    if (!this.growthStatistics) return;

    this.prepareUserGrowthBarChart();
    this.prepareUserGrowthLineChart();
    this.prepareActiveUsersComparisonChart();
    this.prepareSubscriptionPieChart();
    this.prepareAccessSourcePieChart();
    this.prepareAccessSourceGrowthChart();
  }

  private prepareUserGrowthBarChart(): void {
    if (!this.growthStatistics?.userGrowthStatistics || !this.growthStatistics?.activeUserGrowthStatistics) return;

    this.userGrowthBarChartData = {
      labels: this.growthStatistics.userGrowthStatistics.map(x => x.label),
      datasets: [
        {
          label: 'Wszyscy użytkownicy (nowi)',
          data: this.growthStatistics.userGrowthStatistics.map(x => x.newUsersCount),
          backgroundColor: '#42A5F5',
          borderColor: '#1E88E5',
          borderWidth: 1
        },
        {
          label: 'Użytkownicy aktywni (nowi)',
          data: this.growthStatistics.activeUserGrowthStatistics.map(x => x.newUsersCount),
          backgroundColor: '#66BB6A',
          borderColor: '#43A047',
          borderWidth: 1
        }
      ]
    };
  }

  private prepareUserGrowthLineChart(): void {
    if (!this.growthStatistics?.userGrowthStatistics || !this.growthStatistics?.activeUserGrowthStatistics) return;

    this.userGrowthLineChartData = {
      labels: this.growthStatistics.userGrowthStatistics.map(x => x.label),
      datasets: [
        {
          label: 'Wszyscy użytkownicy (łącznie)',
          data: this.growthStatistics.userGrowthStatistics.map(x => x.userCount),
          borderColor: '#42A5F5',
          backgroundColor: 'rgba(66, 165, 245, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Użytkownicy aktywni (łącznie)',
          data: this.growthStatistics.activeUserGrowthStatistics.map(x => x.userCount),
          borderColor: '#66BB6A',
          backgroundColor: 'rgba(102, 187, 106, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }

  private prepareActiveUsersComparisonChart(): void {
    if (!this.growthStatistics?.userGrowthStatistics || !this.growthStatistics?.activeUserGrowthStatistics) return;

  
    this.activeUsersComparisonChartData = {
      labels: this.growthStatistics.userGrowthStatistics.map(x => x.label),
      datasets: [
        {
          label: 'Użytkownicy aktywni (nowi)',
          data: this.growthStatistics.activeUserGrowthStatistics.map(x => x.newUsersCount),
          backgroundColor: '#66BB6A',
          stack: 'Stack 0'
        },
        {
          label: 'Użytkownicy nieaktywni (nowi)',
          data: this.growthStatistics.userGrowthStatistics.map((x, i) => 
            x.newUsersCount - (this.growthStatistics!.activeUserGrowthStatistics![i]?.newUsersCount || 0)
          ),
          backgroundColor: '#FFA726',
          stack: 'Stack 0'
        }
      ]
    };
  }

  private prepareSubscriptionPieChart(): void {
    if (!this.summaryStatistics?.subscriptionStatistics || this.summaryStatistics.subscriptionStatistics.length === 0) return;

    const colors = [
      '#42A5F5',
      '#66BB6A',
      '#FFA726',
      '#EF5350',
      '#AB47BC',
      '#26C6DA',
      '#FFEE58',
      '#EC407A',
      '#78909C'
    ];

    this.subscriptionPieChartData = {
      labels: this.summaryStatistics.subscriptionStatistics.map(x => x.subscriptionPlanName),
      datasets: [{
        data: this.summaryStatistics.subscriptionStatistics.map(x => x.count),
        backgroundColor: colors.slice(0, this.summaryStatistics.subscriptionStatistics!.length)
      }]
    };
  }

  private prepareAccessSourcePieChart(): void {
    if (!this.summaryStatistics) return;

    this.accessSourcePieChartData = {
      labels: ['Płatne subskrypcje', 'Dostępy z książki'],
      datasets: [{
        data: [
          this.summaryStatistics.paidSubscriptionsCount,
          this.summaryStatistics.bookAccessCount
        ],
        backgroundColor: ['#66BB6A', '#FFA726']
      }]
    };
  }

  private prepareAccessSourceGrowthChart(): void {
    if (!this.growthStatistics?.userGrowthStatistics) return;

    this.accessSourceGrowthChartData = {
      labels: this.growthStatistics.userGrowthStatistics.map(x => x.label),
      datasets: [
        {
          label: 'Płatne subskrypcje',
          data: this.growthStatistics.userGrowthStatistics.map(x => x.paidSubscriptionsCount),
          backgroundColor: '#66BB6A',
          borderColor: '#43A047',
          borderWidth: 1
        },
        {
          label: 'Dostępy z książki',
          data: this.growthStatistics.userGrowthStatistics.map(x => x.bookAccessCount),
          backgroundColor: '#FFA726',
          borderColor: '#FB8C00',
          borderWidth: 1
        }
      ]
    };
  }

  public applyFilters(): void {
    this.loadGrowthStatistics();
  }

  public resetFilters(): void {
    this.initializeDateRange();
    this.selectedGrouping = StatisticsGroupingType.Monthly;
    this.loadGrowthStatistics();
  }

  public getActiveUsersPercentage(): number {
    if (!this.summaryStatistics || this.summaryStatistics.totalUsersCount === 0) return 0;
    return (this.summaryStatistics.activeUsersCount / this.summaryStatistics.totalUsersCount) * 100;
  }

  public getPaidSubscriptionsPercentage(): number {
    if (!this.summaryStatistics) return 0;
    const total = this.summaryStatistics.paidSubscriptionsCount + this.summaryStatistics.bookAccessCount;
    if (total === 0) return 0;
    return (this.summaryStatistics.paidSubscriptionsCount / total) * 100;
  }

  public getBookAccessPercentage(): number {
    if (!this.summaryStatistics) return 0;
    const total = this.summaryStatistics.paidSubscriptionsCount + this.summaryStatistics.bookAccessCount;
    if (total === 0) return 0;
    return (this.summaryStatistics.bookAccessCount / total) * 100;
  }

  public getPaidSubscriptionsRevenuePercentage(): number {
    if (!this.summaryStatistics || this.summaryStatistics.totalRevenue === 0) return 0;
    return (this.summaryStatistics.totalPaidSubscriptionsRevenue / this.summaryStatistics.totalRevenue) * 100;
  }

  public getBookAccessRevenuePercentage(): number {
    if (!this.summaryStatistics || this.summaryStatistics.totalRevenue === 0) return 0;
    return (this.summaryStatistics.totalBookAccessRevenue / this.summaryStatistics.totalRevenue) * 100;
  }
}
